import {useEffect, useRef, useState} from 'react';
import {Box, Flex, Image, Spacer, Text, useDisclosure} from '@chakra-ui/react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  TablerBook,
  TablerClipboard,
  TablerHome,
  TablerLogout,
  TablerShield,
  TablerTool,
  TablerUser,
} from '../icons';
import {useAuth} from '../contexts/AuthContext';
import {useUser} from '../contexts/UserContext';
import {useChartingQueueCount} from '../hooks/useChartingQueue';
import {useEhrLinked} from '../hooks/useEhrLinked';
import {
  APP_ROUTES,
  APP_SHELL_TABS,
  buildSidebarResetState,
  getAdminAppShellRoute,
  getAppShellActiveTab,
  isAdminAppRoute,
} from './appShellRoutes';
import ProfileModal from './ProfileModal';
import SignOutConfirmModal from './common/SignOutConfirmModal';

const TAB_ICONS: Record<string, any> = {
  home: TablerHome,
  charting: TablerClipboard,
  tools: TablerTool,
  learn: TablerBook,
};

const SIDEBAR_COLLAPSED_WIDTH = 16;
const SIDEBAR_EXPANDED_WIDTH = 60;
const SIDEBAR_BUTTON_SIZE = '44px';
const SIDEBAR_ANCHOR_SIZE = 8;
// Just long enough that skimming the pointer along the rail on the way
// somewhere else doesn't flicker it open; an intentional hover reads as
// instant. Accidental opens are harmless now that expansion overlays the page
// instead of pushing it.
const SIDEBAR_OPEN_DELAY_MS = 60;
const SIDEBAR_CLOSE_DELAY_MS = 120;
// Decelerate curve: the rail moves fastest in the first frames, so expansion
// is mostly done by ~half the duration — the same distance on the default
// `ease` curve read as sluggish because it ramps up slowly after the hover
// delay has already been paid.
const SIDEBAR_EXPAND_DURATION_MS = 150;
const SIDEBAR_EXPAND_EASING = 'cubic-bezier(0.2, 0, 0, 1)';
const SIDEBAR_EXPAND_TRANSITION = `width ${SIDEBAR_EXPAND_DURATION_MS}ms ${SIDEBAR_EXPAND_EASING}, border-radius ${SIDEBAR_EXPAND_DURATION_MS}ms ${SIDEBAR_EXPAND_EASING}`;
// The blurred edge shadow is deliberately NOT in the width transition above:
// a box-shadow on the resizing rail would be re-rasterized every frame, and
// blurred shadows are WebKit's slowest paint (this is what made Safari
// choppy). Instead the shadow lives on a fixed-size sibling layer animated
// with transform + opacity only, which run on the compositor. It slides on
// the same curve the width animates on, so its casting edge tracks the
// rail's right edge frame-for-frame.
const SIDEBAR_SHADOW_TRANSITION = `transform ${SIDEBAR_EXPAND_DURATION_MS}ms ${SIDEBAR_EXPAND_EASING}, opacity ${SIDEBAR_EXPAND_DURATION_MS}ms ${SIDEBAR_EXPAND_EASING}`;
// Chakra spacing units are 0.25rem each.
const SIDEBAR_SHADOW_COLLAPSED_OFFSET = `-${
  (SIDEBAR_EXPANDED_WIDTH - SIDEBAR_COLLAPSED_WIDTH) / 4
}rem`;
const SIDEBAR_ITEM_TRANSITION = 'background 160ms ease';
const SIDEBAR_ICON_TRANSITION = 'color 160ms ease';
const SIDEBAR_LABEL_TRANSITION = 'opacity 120ms ease-out';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SidebarNavButton = ({
  icon: ItemIcon,
  label,
  isExpanded,
  isActive = false,
  onClick,
  accentColor = 'white',
  logo = false,
  badgeCount = 0,
}: Record<string, any>) => {
  const iconOnly = !logo && !isExpanded;
  const showBadge = badgeCount > 0;
  const badgeLabel = badgeCount > 9 ? '9+' : String(badgeCount);
  const button = (
    <Flex
      as="button"
      type="button"
      data-sidebar-nav-button={logo ? undefined : 'true'}
      data-sidebar-icon-only={iconOnly ? 'true' : 'false'}
      data-sidebar-icon-slot="fixed-left"
      aria-label={showBadge ? `${label} (${badgeCount} pending)` : label}
      aria-current={isActive ? 'page' : undefined}
      align="center"
      justify="flex-start"
      w="100%"
      h={SIDEBAR_BUTTON_SIZE}
      mx={0}
      px={2}
      borderRadius="lg"
      color="white"
      bg={isActive ? 'whiteAlpha.300' : 'transparent'}
      cursor={logo ? 'default' : 'pointer'}
      overflow="hidden"
      transition={SIDEBAR_ITEM_TRANSITION}
      onClick={onClick}
      _hover={
        logo
          ? {
              bg: 'transparent',
            }
          : {
              bg: isActive ? 'whiteAlpha.400' : 'whiteAlpha.200',
            }
      }
      _active={
        logo
          ? {
              bg: 'transparent',
            }
          : {
              bg: isActive ? 'whiteAlpha.400' : 'whiteAlpha.200',
            }
      }
      _focusVisible={{
        outline: 'none',
        boxShadow: '0 0 0 2px rgba(255,255,255,0.7)',
      }}
    >
      <Flex
        className="workspace-sidebar-icon"
        position="relative"
        align="center"
        justify="center"
        flexShrink={0}
        w={SIDEBAR_ANCHOR_SIZE}
        h={SIDEBAR_ANCHOR_SIZE}
        color={accentColor}
        lineHeight={0}
        transition={SIDEBAR_ICON_TRANSITION}
        sx={{
          '& svg': {
            display: 'block',
          },
        }}
      >
        <ItemIcon size={22} />
        {showBadge && (
          <Box
            data-testid="sidebar-nav-badge"
            position="absolute"
            top="-5px"
            right="-7px"
            minW="16px"
            h="16px"
            px="4px"
            borderRadius="full"
            bg="white"
            color="opalite.700"
            fontSize="10px"
            fontWeight="bold"
            lineHeight="16px"
            textAlign="center"
            boxShadow="0 0 0 2px var(--chakra-colors-opalite-600)"
            pointerEvents="none"
          >
            {badgeLabel}
          </Box>
        )}
      </Flex>
      <Text
        ml={isExpanded ? (logo ? 4 : 3) : 0}
        maxW={isExpanded ? '12rem' : 0}
        overflow="hidden"
        fontSize={logo ? 'xl' : 'sm'}
        fontWeight={logo ? 600 : 'normal'}
        whiteSpace="nowrap"
        opacity={isExpanded ? 1 : 0}
        transition={SIDEBAR_LABEL_TRANSITION}
        pointerEvents="none"
        aria-hidden={!isExpanded}
        style={
          logo
            ? {
                fontWeight: 600,
                marginLeft: isExpanded ? '1rem' : 0,
              }
            : undefined
        }
      >
        {label}
      </Text>
    </Flex>
  );

  return button;
};

const Sidebar = ({placement = 'fixed'}: Record<string, any>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {logout} = useAuth();
  const {userData, mockEhrMode} = useUser();
  const {
    isOpen: isProfileOpen,
    onOpen: onProfileOpen,
    onClose: onProfileClose,
  } = useDisclosure();
  const {
    isOpen: isSignOutOpen,
    onOpen: onSignOutOpen,
    onClose: onSignOutClose,
  } = useDisclosure();
  const isInline = placement === 'inline';
  const [isExpanded, setIsExpanded] = useState(false);
  const openDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPointerInsideRef = useRef(false);
  const isModalPinnedRef = useRef(false);
  const activeTab = getAppShellActiveTab(location.pathname);
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';
  // Charting is an EHR-only workflow (its whole point is pushing notes to the
  // EHR), so the tab only appears for EHR-linked accounts.
  const ehrLinked = useEhrLinked();
  const chartingPendingCount = useChartingQueueCount(
    ehrLinked,
    userData?.role === 'superadmin' && mockEhrMode,
  );
  const visibleTabs = APP_SHELL_TABS.filter(
    tab => tab.id !== 'charting' || ehrLinked,
  );

  const navigateToSidebarPage = (route: string) => {
    // Touch taps fire the emulated mouseenter (expanding the rail) but never
    // a matching mouseleave, so after navigating the rail sat expanded over
    // the new page. Collapse explicitly whenever a destination is chosen.
    clearOpenDelay();
    clearCloseDelay();
    clearNavDelay();

    const goToRoute = () => navigate(route, {state: buildSidebarResetState()});

    // When the rail is open, play the same width collapse a pointer-leave uses
    // before swapping pages. Firing navigate() in the same tick let the route's
    // re-render clobber the transition, so clicking an icon snapped the rail
    // shut instantly while mouse-leave eased it closed — they should match.
    if (isExpanded && !prefersReducedMotion()) {
      setIsExpanded(false);
      navDelayRef.current = setTimeout(() => {
        navDelayRef.current = null;
        goToRoute();
      }, SIDEBAR_EXPAND_DURATION_MS);
      return;
    }

    setIsExpanded(false);
    goToRoute();
  };

  useEffect(
    () => () => {
      if (openDelayRef.current !== null) {
        clearTimeout(openDelayRef.current);
      }
      if (closeDelayRef.current !== null) {
        clearTimeout(closeDelayRef.current);
      }
      if (navDelayRef.current !== null) {
        clearTimeout(navDelayRef.current);
      }
    },
    [],
  );

  const clearOpenDelay = () => {
    if (openDelayRef.current !== null) {
      clearTimeout(openDelayRef.current);
      openDelayRef.current = null;
    }
  };

  const clearCloseDelay = () => {
    if (closeDelayRef.current !== null) {
      clearTimeout(closeDelayRef.current);
      closeDelayRef.current = null;
    }
  };

  const clearNavDelay = () => {
    if (navDelayRef.current !== null) {
      clearTimeout(navDelayRef.current);
      navDelayRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    isPointerInsideRef.current = true;
    clearOpenDelay();
    clearCloseDelay();
    if (isExpanded) return;
    openDelayRef.current = setTimeout(() => {
      setIsExpanded(true);
      openDelayRef.current = null;
    }, SIDEBAR_OPEN_DELAY_MS);
  };

  const handleMouseLeave = () => {
    isPointerInsideRef.current = false;
    clearOpenDelay();
    if (isModalPinnedRef.current) return;
    clearCloseDelay();
    closeDelayRef.current = setTimeout(() => {
      closeDelayRef.current = null;
      if (!isPointerInsideRef.current && !isModalPinnedRef.current) {
        setIsExpanded(false);
      }
    }, SIDEBAR_CLOSE_DELAY_MS);
  };

  const openSidebarModal = (openModal: () => void) => {
    clearOpenDelay();
    clearCloseDelay();
    isModalPinnedRef.current = true;
    setIsExpanded(true);
    openModal();
  };

  const closeSidebarModal = (closeModal: () => void) => {
    closeModal();
    isModalPinnedRef.current = false;
    if (!isPointerInsideRef.current) {
      setIsExpanded(false);
    }
  };

  // The rail is positioned out of the page flow in both placements (absolute
  // inside a collapsed-width placeholder when inline, fixed otherwise), so
  // hover-expansion overlays the content instead of resizing it — animating
  // `width` on an in-flow rail re-laid-out the entire workspace every frame.
  const rail = (
    <>
      {/*
       * Compositor-only shadow layer for the expanded rail (see
       * SIDEBAR_SHADOW_TRANSITION). Fixed at the expanded width behind the
       * rail; collapsed it is translated left so its right edge sits exactly
       * under the collapsed rail's, then it slides out and fades in as the
       * rail expands. Transparent background: an outer box-shadow only paints
       * outside the border box, so only the blur is visible.
       */}
      <Box
        aria-hidden="true"
        position={isInline ? 'absolute' : 'fixed'}
        left={0}
        top={isInline ? 0 : '60px'}
        h={isInline ? '100%' : 'calc(100vh - 60px)'}
        w={SIDEBAR_EXPANDED_WIDTH}
        zIndex={8}
        pointerEvents="none"
        borderRightRadius="xl"
        boxShadow="8px 0 24px -12px rgba(15, 23, 42, 0.5)"
        opacity={isExpanded ? 1 : 0}
        transform={
          isExpanded
            ? 'translateX(0)'
            : `translateX(${SIDEBAR_SHADOW_COLLAPSED_OFFSET})`
        }
        sx={{
          transition: SIDEBAR_SHADOW_TRANSITION,
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
        }}
      />
      <Flex
        as="nav"
        aria-label="Workspace navigation"
        direction="column"
        bg="opalite.600"
        color="white"
        w={isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH}
        h={isInline ? '100%' : 'calc(100vh - 60px)'}
        position={isInline ? 'absolute' : 'fixed'}
        left={0}
        top={isInline ? 0 : '60px'}
        // Collapsed, the rail stays square so the content cards' rounded left
        // corners notch into it seamlessly; expanded, it overlays the page as
        // a flyout panel, so its outward edge picks up the card radius.
        borderRightRadius={isExpanded ? 'xl' : 0}
        overflow="hidden"
        px={2}
        pt={{base: 4, lg: 5}}
        pb={3}
        zIndex={9}
        // Promotes the rail onto its own compositing layer. Width is a
        // layout property, so the animation still repaints the rail each
        // frame — but without this, Safari also re-rasterizes the page
        // content the expanding rail overlays, which is the expensive part.
        transform="translateZ(0)"
        sx={{
          transition: SIDEBAR_EXPAND_TRANSITION,
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <SidebarNavButton
          isExpanded={isExpanded}
          logo
          icon={() => (
            <Image
              src="/Opalite_Health_logo_v1_tight.png"
              alt=""
              boxSize="34px"
              objectFit="contain"
              filter="brightness(0) invert(1)"
            />
          )}
          label="Opalite Health"
          onClick={() => navigateToSidebarPage(APP_ROUTES.home)}
        />

        <Flex
          data-testid="sidebar-primary-nav"
          direction="column"
          position="absolute"
          top="50%"
          left={2}
          right={2}
          gap={1}
          transform="translateY(-50%)"
        >
          {visibleTabs.map(tab => (
            <SidebarNavButton
              key={tab.id}
              icon={TAB_ICONS[tab.id]}
              label={tab.label}
              isExpanded={isExpanded}
              isActive={activeTab === tab.id}
              badgeCount={tab.id === 'charting' ? chartingPendingCount : 0}
              onClick={() => navigateToSidebarPage(tab.route)}
            />
          ))}
        </Flex>

        <Spacer />

        <Flex direction="column" w="100%" gap={1}>
          {isAdmin && (
            <SidebarNavButton
              icon={TablerShield}
              label="Admin"
              accentColor="gray.200"
              isExpanded={isExpanded}
              isActive={isAdminAppRoute(location.pathname)}
              onClick={() =>
                navigateToSidebarPage(getAdminAppShellRoute(userData?.role))
              }
            />
          )}

          <SidebarNavButton
            icon={TablerUser}
            label="Profile"
            isExpanded={isExpanded}
            onClick={() => openSidebarModal(onProfileOpen)}
          />

          <SidebarNavButton
            icon={TablerLogout}
            label="Sign Out"
            isExpanded={isExpanded}
            onClick={() => openSidebarModal(onSignOutOpen)}
          />
        </Flex>
      </Flex>
      <ProfileModal
        isOpen={isProfileOpen}
        onClose={() => closeSidebarModal(onProfileClose)}
      />
      <SignOutConfirmModal
        isOpen={isSignOutOpen}
        onClose={() => closeSidebarModal(onSignOutClose)}
        onConfirm={logout}
      />
    </>
  );

  if (!isInline) return rail;

  // Stable collapsed-width placeholder that owns the rail's slot in the
  // shell's flex row; the absolutely-positioned rail expands over the content
  // to its right without moving it. Hidden below `lg`, where the native-style
  // bottom bar keeps phones and portrait tablets single-column.
  return (
    <Box
      w={SIDEBAR_COLLAPSED_WIDTH}
      h="100%"
      flexShrink={0}
      position="relative"
      display={{base: 'none', lg: 'block'}}
    >
      {rail}
    </Box>
  );
};

export default Sidebar;
